import {StateCreator} from "zustand/vanilla";
import {Comment, CommentAssociations, CommentThread, ThreadWithComments} from "@/pb/api/devplan/types/comments";
import {commentAPI, commentsThreadAPI, commentsThreadsByDocAPI} from "@/app/apiPaths";
import {
  DeleteCommentRequest,
  DeleteCommentResponse,
  GetDocumentCommentThreadsResponse,
  GetThreadCommentsResponse,
  UpdateCommentsThreadRequest,
  UpdateCommentsThreadResponse,
  UpsertCommentRequest,
  UpsertCommentResponse
} from "@/pb/api/devplan/services/web/company";
import logger from "@/app/logger";

export interface CommentsThreadInfo {
  thread: CommentThread | undefined
  comments: Record<string, Comment>
}

export type CommentsStoreState = {
  commentThreads: Record<string, CommentsThreadInfo>;
  upsertComment(comment: Comment, associations?: CommentAssociations): Promise<Comment>;
  deleteComment(companyId: number, threadId: string, id: string): Promise<void>;
  fetchCommentsThread(companyId: number, threadId: string): Promise<void>;
  fetchCommentsThreadForDoc(companyId: number, documentId: string): Promise<ThreadWithComments[]>;
  updateCommentsThread(companyId: number, id: string, updater: (t: CommentThread) => CommentThread | undefined): Promise<void>;
}

export const createCommentsSlice: StateCreator<
  CommentsStoreState,
  [],
  [],
  CommentsStoreState
> = ((set, get) => ({
  commentThreads: {},
  fetchCommentsThread: async (companyId, threadId) => fetch(commentsThreadAPI(companyId, threadId))
    .then(r => r.ok ? r.json() : Promise.reject(r.statusText))
    .then(js => {
      const {thread} = GetThreadCommentsResponse.fromJSON(js);
      const comments = thread?.comments.reduce((a, c) => ({...a, [c.id]: c}), {} as Record<string, Comment>) || {}
      set(s => ({...s, commentThreads: {...s.commentThreads, [threadId]: {thread: thread?.thread, comments}}}))
    }),
  upsertComment: async (comment, associations) => fetch(commentAPI(comment.companyId), {
    method: 'POST', body: JSON.stringify(UpsertCommentRequest.toJSON({comment, associations}))
  }).then(r => r.ok ? r.json() : Promise.reject(new Error(r.statusText)))
    .then(js => {
      const resp = UpsertCommentResponse.fromJSON(js);
      const {thread, comments} = resp.thread!
      set(s => {
        const newComments = comments.reduce((a, c) => ({...a, [c.id]: c}), {} as Record<string, Comment>)
        return {
          ...s,
          commentThreads: {...s.commentThreads, [thread!.id]: {thread: thread!, comments: newComments}}
        }
      })
      return comments.find(c => c.id === resp.commentId)!
    }),
  deleteComment: async (companyId, threadId, id) => fetch(commentAPI(companyId), {
    method: 'DELETE', body: JSON.stringify(DeleteCommentRequest.toJSON({id}))
  }).then(r => r.ok ? r.json() : Promise.reject(new Error(r.statusText)))
    .then((js) => set(s => {
        const {thread} = DeleteCommentResponse.fromJSON(js)
        const comments = thread?.comments.reduce((a, c) => ({...a, [c.id]: c}), {} as Record<string, Comment>) || {}
        return {...s, commentThreads: {...s.commentThreads, [threadId]: {thread: thread?.thread, comments}}}
      })
    ),
  updateCommentsThread: async (companyId, id, updater) => {
    const existing = get().commentThreads[id]?.thread
    if (!existing) {
      logger.info({companyId, id}, "No comments thread, can't update.")
      return
    }
    const updated = updater(existing)
    if (!updated) {
      logger.debug({companyId, id}, "Comment thread update skipped.")
      return
    }
    await fetch(commentsThreadAPI(companyId, id), {
      method: 'POST', body: JSON.stringify(UpdateCommentsThreadRequest.toJSON({thread: updated}))
    }).then(r => r.ok ? r.json() : Promise.reject(new Error(r.statusText)))
      .then(js => {
        const {thread} = UpdateCommentsThreadResponse.fromJSON(js)
        set(s => {
          const info = s.commentThreads[id]
          return {...s, commentThreads: {...s.commentThreads, [id]: {...info, thread}}}
        })
        logger.debug({thread}, "Comment thread updated.")
      })
  },
  fetchCommentsThreadForDoc: async (companyId, documentId) => fetch(commentsThreadsByDocAPI(companyId, documentId))
    .then(r => r.ok ? r.json() : Promise.reject(r.statusText))
    .then(js => {
      const {threads} = GetDocumentCommentThreadsResponse.fromJSON(js)
      const updates: Record<string, CommentsThreadInfo> = {}
      threads.forEach(t => {
        const comments = t.comments.reduce((a, c) => ({...a, [c.id]: c}), {} as Record<string, Comment>)
        updates[t.thread!.id] = {thread: t.thread, comments}
      })
      set(s => ({...s, commentThreads: {...s.commentThreads, ...updates}}))
      return threads
    })
}))